import * as React from "react";
import type { DrawerSelectEvent, MenuSelectEvent } from "@progress/kendo-react-layout";
import { Avatar, Drawer, DrawerContent, Menu, MenuItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { useNavigate, useLocation } from "react-router-dom";
import { userIcon, logoutIcon, menuIcon } from "@progress/kendo-svg-icons";
import type { SidebarProps } from "./SidebarProps";
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import "./styles.css";
import { useEffect } from "react";
import { AuthService } from "@infrastructure/services";
import { useInjection } from "@presentation/components/dependencyInjection";
import { logOutInProgress } from "@presentation/components/helpers/constants";
import { ProfileWindow } from "./ProfileWindow"; // ← adjust path if needed

const Sidebar: React.FC<SidebarProps> = (props) => {

  const { setSessionState, username } = useUserManagementStore((state) => ({
    setSessionState: state.setSessionState,
    username: state.userSession?.username ?? "Undefined",
  }));

  const navigate = useNavigate();
  const authService = useInjection(AuthService);

  const [selectedIndex, setSelectedIndex] = React.useState<number>(() => {
    const jobListIndex = props.routes.findIndex((route) => route.text === "Job List");
    const savedIndex = localStorage.getItem("selectedIndexSidebarIndex");
    if (savedIndex) {
      const parsed = Number.parseInt(savedIndex, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    return jobListIndex >= 0 ? jobListIndex : 0;
  });

  const [isExpanded, setIsExpanded] = React.useState<boolean>(localStorage.getItem("sidebarisExpanded") === "true");
  const [showProfileWindow, setShowProfileWindow] = React.useState<boolean>(false);

  const selectedRouteText = props.routes[selectedIndex]?.text ?? "";

  useEffect(() => {
    localStorage.setItem("selectedIndexSidebarIndex", String(selectedIndex));
  }, [selectedIndex]);

  useEffect(() => {
    localStorage.setItem("sidebarisExpanded", String(isExpanded));
  }, [isExpanded]);

  const location = useLocation();

  useEffect(() => {
    const routeIndex = props.routes.findIndex((route) => route.route === location.pathname);
    if (routeIndex >= 0 && routeIndex !== selectedIndex) {
      setSelectedIndex(routeIndex);
    }
  }, [location.pathname, props.routes, selectedIndex]);

  const handleClick = React.useCallback((): void => {
    setIsExpanded((prev) => !prev);
  }, []);

  const onSelect = React.useCallback(
    (e: DrawerSelectEvent): void => {
      const selectedRoute = props.routes[e.itemIndex]?.route;
      if (selectedRoute) {
        navigate(selectedRoute);
      }
      setSelectedIndex(e.itemIndex);
    },
    [navigate, props.routes],
  );

  const onMenuItemSelect = React.useCallback(
    (event: MenuSelectEvent): void => {
      const text = event.item.text; // Kendo MenuItem supports an optional `text` prop
      if (text === "Logout") {
        sessionStorage.setItem(logOutInProgress, "true");
        setSessionState(null, null, null);
        try {
          authService.signOut();
        } catch (err) {
          // keep this broad; signOut implementation may vary
          console.warn("Sign out failed:", err);
        }
        navigate("/", { replace: true });
      } else if (text === "My Profile") {
        setShowProfileWindow(true);
      }
    },
    [authService, navigate, setSessionState],
  );

  const renderMenuItem = React.useCallback(
    () => (
      <span className="username">
        <Avatar rounded="full" type="icon" className="user-avatar">
          <SvgIcon icon={userIcon} />
        </Avatar>
        {username}
      </span>
    ),
    [username],
  );

  return (
    <div className="main-container">
      <div className="header-toolbar">
        <div className="flex-center">
          <Button className="header-button" themeColor={"primary"} svgIcon={menuIcon} onClick={handleClick} />
          <span className="flex-center">
            <img src="/tab_omicsFlowLogo_RGB2_16x16.svg" className="header-logo" alt="OmicsFlow logo" />
            <div className="header-title">OmicsFlow</div>
            <div className="header-subtitle">- {selectedRouteText}</div>
          </span>
        </div>

        <div className="flex-center">
          <Menu onSelect={onMenuItemSelect}>
            <MenuItem render={renderMenuItem}>
              <MenuItem svgIcon={userIcon} text="My Profile" />
              <MenuItem svgIcon={logoutIcon} text="Logout" />
            </MenuItem>
          </Menu>
          <div className="push-left">&nbsp;</div>
        </div>
      </div>

      <Drawer
        className="main-container"
        expanded={isExpanded}
        position={"start"}
        mode={"push"}
        mini={true}
        items={props.routes.map((item, index) => ({
          ...item,
          selected: index === selectedIndex,
        }))}
        onSelect={onSelect}
      >
        <DrawerContent className="drawer-content">{props.routes[selectedIndex]?.element ?? props.children}</DrawerContent>
      </Drawer>

      {showProfileWindow && <ProfileWindow username={username} onClose={() => setShowProfileWindow(false)} />}
    </div>
  );
};

export { Sidebar };
